'use client';

import { useEffect, useRef } from 'react';
import { GalleryVertical, List, Map } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';

import { LucideIcon } from '@/src/components/LucideIcon';
import { css } from '@/styled-system/css';
import { token } from '@/styled-system/tokens';

export type MobileSrpControlValue = 'map' | 'grid' | 'feed';

type Props = {
  value: MobileSrpControlValue;
  onValueChange: (value: MobileSrpControlValue) => void;
};

const MOBILE_SRP_CONTROLS_Y_ADJUST_VAR = '--mobile-srp-controls-y-adjust';

const controlsWrapper = css({
  position: 'fixed',
  left: '50%',
  transform: `translate(-50%, var(${MOBILE_SRP_CONTROLS_Y_ADJUST_VAR}, 0))`,
  bottom: `calc(env(safe-area-inset-bottom) + ${token.var('spacing.default')})`,
  zIndex: 'zIndexOverParent',
  display: 'flex',
  maxWidth: `calc(100vw - ${token.var('spacing.loose')})`,
  borderRadius: 'pill',
  boxShadow: 'smHeavy',
  lg: {
    display: 'none',
  },
});

const controlsGroupStyles = css.raw({
  display: 'flex',
  overflow: 'hidden',
  borderRadius: 'pill',
  // Constellation's conjoined ToggleButtonGroup squares child edges, with no prop/token
  // for preserving a pill outline on only the outer mobile nav edges.
  '& > [data-c11n-component="ToggleButton.Root"]:first-child': {
    borderTopLeftRadius: 'pill',
    borderBottomLeftRadius: 'pill',
  },
  '& > [data-c11n-component="ToggleButton.Root"]:last-child': {
    borderTopRightRadius: 'pill',
    borderBottomRightRadius: 'pill',
  },
});

const controlButtonStyles = css.raw({
  flex: '1 1 0',
  minWidth: '2600',
  whiteSpace: 'nowrap',
});

export function MobileSrpBottomControls({ value, onValueChange }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('search');

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    let animationFrame = 0;

    const updateVisualViewportOffset = () => {
      animationFrame = 0;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const currentAdjustment = parseFloat(wrapper.style.getPropertyValue(MOBILE_SRP_CONTROLS_Y_ADJUST_VAR)) || 0;
      const unadjustedBottom = wrapper.getBoundingClientRect().bottom - currentAdjustment;
      const unadjustedGap = visualViewport.height - unadjustedBottom;
      const desiredGap = parseFloat(window.getComputedStyle(wrapper).bottom) || 0;
      const nextAdjustment = Math.max(0, Math.ceil(unadjustedGap - desiredGap));

      wrapper.style.setProperty(MOBILE_SRP_CONTROLS_Y_ADJUST_VAR, nextAdjustment > 0 ? `${nextAdjustment}px` : '0');
    };

    const requestVisualViewportOffsetUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(updateVisualViewportOffset);
    };

    requestVisualViewportOffsetUpdate();
    visualViewport.addEventListener('resize', requestVisualViewportOffsetUpdate);
    visualViewport.addEventListener('scroll', requestVisualViewportOffsetUpdate);
    window.addEventListener('resize', requestVisualViewportOffsetUpdate);

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      visualViewport.removeEventListener('resize', requestVisualViewportOffsetUpdate);
      visualViewport.removeEventListener('scroll', requestVisualViewportOffsetUpdate);
      window.removeEventListener('resize', requestVisualViewportOffsetUpdate);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={controlsWrapper}
      data-testid="mobile-srp-controls"
      data-srp-component="mobile-srp-controls"
    >
      <ToggleButtonGroup
        aria-label={t('viewMode')}
        value={value}
        onValueChange={(nextValue) => {
          if (typeof nextValue !== 'string') return;
          onValueChange(nextValue as MobileSrpControlValue);
        }}
        size="sm"
        css={controlsGroupStyles}
      >
        <ToggleButton
          value="map"
          icon={<LucideIcon icon={Map} />}
          data-testid="mobile-srp-control-map"
          css={controlButtonStyles}
        >
          {t('mobileLabelMap')}
        </ToggleButton>
        <ToggleButton
          value="grid"
          icon={<LucideIcon icon={List} />}
          data-testid="mobile-srp-control-list"
          css={controlButtonStyles}
        >
          {t('viewModeList')}
        </ToggleButton>
        <ToggleButton
          value="feed"
          icon={<LucideIcon icon={GalleryVertical} />}
          data-testid="mobile-srp-control-feed"
          css={controlButtonStyles}
        >
          {t('viewModeFeed')}
        </ToggleButton>
      </ToggleButtonGroup>
    </div>
  );
}
